import { useEffect, useMemo, useRef, useState } from "react";
import { format, isSameDay } from "date-fns";
import { AlertCircle } from "lucide-react";
import Modal from "@/components/ui/Modal";
import { CustomButton } from "@/components/ui/Buttons";
import toast from "react-hot-toast";
import { useGlobalContext } from "@/context/GlobalContext";
import DateSelector from "./DateSelector";
import AddressSection from "./AddressSection";
import ScopeSelector from "./ScopeSelector";
import NotesCard from "./NotesCard";

import DaySlotEditor from "./DaySlotEditor";
import SelectedSlotsList from "./SelectedSlotsList";

import { DateAvailability, ScopeType } from "./types";

import {
  useGetAvailabilityByCreatorIdQuery,
  useUploadSameDayDeliveryDataMutation,
  useUpdateSameDayAvailableCreatorMutation,
} from "@/redux/feature/sameDayDelivery/sameDayDeliveryApi";
import { useGetShippingAddressesQuery } from "@/redux/feature/shippingAddress/shippingAddressApi";

import ClockIcon from "@/assets/icons/ClockGradient.svg";
import { toUtcStartOfDay } from "./date";
import { enrichBackendSlot } from "./availabilityUtils";

interface AvailabilityModalProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onConfirm?: (data: {
    availability: DateAvailability[];
    addressId: string | null;
    scope: ScopeType;
    agreedToTerms: boolean;
  }) => void;
}

const AvailabilityModal = ({ isOpen, setOpen }: AvailabilityModalProps) => {
  const { userData } = useGlobalContext();

  // purely for display badge (local)
  const today = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  // ✅ stable "todayUtc" so effects don't re-run and snap back to today
  const todayUtc = useMemo(() => toUtcStartOfDay(new Date()), []);

  const [availability, setAvailability] = useState<DateAvailability[]>([]);
  const [activeDate, setActiveDate] = useState<Date>(todayUtc);

  const [selectedAddressId, setSelectedAddressId] = useState<string | null>(
    null,
  );
  const [selectedScope, setSelectedScope] = useState<ScopeType>(null);
  const [agreedToTerms, setAgreedToTerms] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const { data: existingDTO, isLoading: isAvailabilityQueryLoading, isFetching } =
    useGetAvailabilityByCreatorIdQuery(undefined, {
      skip: !userData?.creator_id,
    });
  const existingData = existingDTO?.data;

  const [createSameDayAvailableCreator] =
    useUploadSameDayDeliveryDataMutation();
  const [updateSameDayAvailableCreator] =
    useUpdateSameDayAvailableCreatorMutation();

  // highlight dates that have saved slots
  const savedDates = useMemo(() => {
    return availability
      .filter((a) => a.isAllDay || (a.timeSlots?.length || 0) > 0)
      .map((a) => a.date);
  }, [availability]);

  // ✅ One-time hydration per modal open (prevents re-setting activeDate after user clicks)
  const hasHydratedRef = useRef(false);

  // reset hydration gate on close
  useEffect(() => {
    if (!isOpen) hasHydratedRef.current = false;
  }, [isOpen]);

  // ✅ On open: hydrate from backend OR defaults ONCE, after the query has settled.
  // If we lock hydration while `existingData` is still loading (e.g. modal open on first paint),
  // we would never apply server data — dashboard "works" because the modal opens later.
  useEffect(() => {
    if (!isOpen) return;
    if (!userData?.creator_id) return;
    if (hasHydratedRef.current) return;
    if (isAvailabilityQueryLoading || isFetching) return;

    hasHydratedRef.current = true;

    if (existingData) {
      setSelectedAddressId(existingData.address_id);
      setSelectedScope(existingData.delivery_scope);
      setAgreedToTerms(Boolean(existingData.agreedToTerms ?? true));

      const mappedAvailability: DateAvailability[] = (
        existingData.availability || []
      ).map((a: any) => ({
        date: toUtcStartOfDay(new Date(a.date)),
        isAllDay: Boolean(a.isAllDay),
        timeSlots: (a.timeSlots || []).map((s: any) =>
          enrichBackendSlot({
            id: s.id || crypto.randomUUID(),
            start: s.start,
            end: s.end,
          }),
        ),
      }));

      setAvailability(mappedAvailability);
      setActiveDate(todayUtc); // keep default to today (draft allowed)
    } else {
      // no backend yet
      setAvailability([]); // start empty; date becomes "saved" only after save
      setSelectedAddressId(null);
      setSelectedScope(null);
      setAgreedToTerms(true);
      setActiveDate(todayUtc); // ✅ default today
    }
  }, [
    isOpen,
    existingData,
    todayUtc,
    userData?.creator_id,
    isAvailabilityQueryLoading,
    isFetching,
  ]);

  // ✅ IMPORTANT: always return a draft availability object for the active date
  const getDateAvail = (d: Date): DateAvailability => {
    return (
      availability.find((a) => isSameDay(a.date, d)) || {
        date: d,
        isAllDay: false,
        timeSlots: [],
      }
    );
  };

  // ✅ Save from editor: if empty -> remove saved day, else upsert
  const upsertAvailability = (updated: DateAvailability) => {
    const hasSlots = updated.isAllDay || (updated.timeSlots?.length || 0) > 0;

    setAvailability((prev) => {
      const exists = prev.some((a) => isSameDay(a.date, updated.date));

      if (!hasSlots) {
        return prev.filter((a) => !isSameDay(a.date, updated.date));
      }

      if (!exists) return [...prev, updated];
      return prev.map((a) => (isSameDay(a.date, updated.date) ? updated : a));
    });
  };

  const handleDeleteDate = (date: Date) => {
    setAvailability((prev) => prev.filter((a) => !isSameDay(a.date, date)));
    // keep editor open on that date, but it becomes draft
    setActiveDate(toUtcStartOfDay(date));
  };

  const getApiErrorMessage = (err: any) => {
    const msg =
      err?.data?.message ||
      err?.data?.error ||
      err?.error ||
      err?.message ||
      "Something went wrong. Please try again.";
    return String(msg);
  };

  const handleConfirm = async () => {
    setIsLoading(true);

    // No slots = visibility off (profile won't show). Also disable if address is invalid.
    const noSlots = availability.length === 0;
    const profileVisibility = noSlots || shouldDisableVisibility ? false : true;

    const payload = {
      creator_id: userData?.creator_id,
      availability: availability.map((a) => ({
        date: toUtcStartOfDay(new Date(a.date)),
        isAllDay: a.isAllDay,
        timeSlots: (a.timeSlots || []).map((s) => ({
          id: s.id,
          start: s.start,
          end: s.end,
        })),
      })),
      address_id: selectedAddressId,
      delivery_scope: selectedScope,
      agreedToTerms,
      isProfileVisible: profileVisibility,
    };

    try {
      if (existingData) {
        await updateSameDayAvailableCreator(payload).unwrap();
        if (noSlots) {
          toast.success("All slots removed. Profile visibility is now off.");
        } else if (shouldDisableVisibility && existingData.isProfileVisible) {
          toast.success("Availability updated. Profile visibility disabled due to invalid address.");
        } else {
          toast.success("Availability updated successfully");
        }
      } else {
        await createSameDayAvailableCreator(payload).unwrap();
        toast.success(noSlots ? "Saved. Profile visibility is off." : "Availability created successfully");
      }

      setIsLoading(false);
      setOpen(false);
    } catch (error: any) {
      console.error("Availability submit error:", error);
      toast.error(getApiErrorMessage(error));
      setIsLoading(false);
    }
  };

  // Fetch addresses to check if selection is required
  const { data: addressesData } = useGetShippingAddressesQuery(
    encodeURIComponent(userData?.email || ""),
    { skip: !userData?.email },
  );
  const addresses = addressesData?.data || [];
  const hasAddresses = addresses.length > 0;

  // ✅ Clear selectedAddressId if it no longer exists in the addresses list
  useEffect(() => {
    if (selectedAddressId && addresses.length > 0) {
      const addressExists = addresses.some((addr: any) => addr._id === selectedAddressId);
      if (!addressExists) {
        setSelectedAddressId(null);
      }
    }
  }, [addresses, selectedAddressId]);

  const hasSlots = useMemo(
    () =>
      availability.some(
        (a) => a.isAllDay || (a.timeSlots?.length || 0) > 0,
      ),
    [availability],
  );

  const isValid = useMemo(() => {
    // Address selection is required if there are addresses available (when we have slots)
    const selectedAddressExists = selectedAddressId
      ? addresses.some((addr: any) => addr._id === selectedAddressId)
      : false;
    const hasValidAddress = hasAddresses
      ? (selectedAddressId !== null && selectedAddressExists)
      : false;

    // With slots: require availability + address + scope + terms
    if (hasSlots) {
      return selectedScope && agreedToTerms && hasValidAddress;
    }
    // No slots (removing all): allow confirm to turn visibility off; only require terms
    return agreedToTerms;
  }, [hasSlots, availability, selectedScope, agreedToTerms, selectedAddressId, hasAddresses, addresses]);

  // Check if profile visibility should be disabled due to invalid address
  const shouldDisableVisibility = useMemo(() => {
    if (!existingData) return false;
    if (!hasAddresses) return true;
    if (!selectedAddressId) return true;
    const selectedExists = addresses.some((addr: any) => addr._id === selectedAddressId);
    return !selectedExists;
  }, [existingData, hasAddresses, selectedAddressId, addresses]);

  return (
    <Modal isOpen={isOpen} setOpen={setOpen} width="560px" mobileFullscreen>
      <div className="space-y-5 pt-2 pb-32 sm:space-y-6 sm:pt-0 sm:pb-2">
        <div className="flex flex-col gap-2 sm:gap-3">
          {/* Header Row */}
          <div className="flex items-start sm:items-center gap-3 flex-wrap">
            <ClockIcon />

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                <h2 className="text-base sm:text-lg font-semibold tracking-normal text-[#101828]">
                  Confirm your Availability
                </h2>

                <span className="w-fit px-2.5 py-[3px] rounded-full border border-accent text-[11px] sm:text-[12px] font-medium text-[#ED5E00] whitespace-nowrap">
                  {format(today, "EEE, do MMMM")}
                </span>
              </div>
            </div>
          </div>

          {/* Description */}
          <p className="text-[13px] sm:text-sm text-[#475467] leading-relaxed max-w-[680px]">
            Select the time slots you're available today to receive same-day
            projects. You'll only get client briefs during the slots you
            confirm.
          </p>
        </div>

        <DateSelector
          activeDate={activeDate}
          savedDates={savedDates}
          onSelectDate={(d) => setActiveDate(toUtcStartOfDay(d))}
        />

        <DaySlotEditor
          dateAvail={getDateAvail(activeDate)}
          onSave={(updated) => {
            upsertAvailability(updated);
          }}
        />

        <SelectedSlotsList
          availability={availability}
          onEditDate={(d) => setActiveDate(toUtcStartOfDay(d))}
          onDeleteDate={handleDeleteDate}
        />

        <AddressSection
          selectedAddressId={selectedAddressId}
          onSelectAddress={setSelectedAddressId}
          onAddAddress={() => {}}
        />

        <NotesCard />

        <ScopeSelector
          selectedScope={selectedScope}
          onSelectScope={setSelectedScope}
          shootAndEditPrice={2200}
          shootOnlyPrice={1800}
        />

        {/* Profile Visibility Warning */}
        {shouldDisableVisibility && existingData?.isProfileVisible && (
          <div className="p-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">
                  Profile Visibility Will Be Disabled
                </p>
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  {!hasAddresses 
                    ? "Your profile will be hidden from clients because no addresses are available. Add an address to maintain visibility."
                    : "Your profile will be hidden from clients because the selected address is no longer valid. Please select a valid address to maintain visibility."
                  }
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="fixed z-20 bottom-0 left-0 right-0 px-5 pt-4 pb-[max(20px,env(safe-area-inset-bottom))] bg-white border-t border-[#EAECF0] shadow-[0_-8px_20px_rgba(16,24,40,0.06)] flex gap-3 sm:static sm:border-0 sm:bg-transparent sm:p-0 sm:pt-2 sm:shadow-none">
          <CustomButton
            variant="outline"
            className="flex-1"
            onClick={() => setOpen(false)}
          >
            Cancel
          </CustomButton>

          <CustomButton
            className="flex-1 bg-primary hover:bg-primary/90"
            onClick={handleConfirm}
            disabled={!isValid || isLoading}
          >
            {isLoading
              ? existingData
                ? "Updating..."
                : "Confirming..."
              : "Confirm Slots"}
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default AvailabilityModal;
